import { Injectable } from '@angular/core';
import { ApiService } from '../../Api/Service/api.service';
import { CookieService } from 'ngx-cookie-service';
import { AuthToken } from '../Models/AuthToken.model';
import { environment } from '../../../../environments/environment';
import { Observable } from 'rxjs';
import { map, switchMap, catchError, tap } from 'rxjs/operators';
import { CookieOptions } from '../Models/CookieOptions.model';
import { ProfileService } from '../../Profile/Service/profile.service';
import { RolesService } from '../../Roles/Service/roles.service';
import { SessionService } from '../../Session/session.service';
import { SearchStateService } from '../../../Shared/SearchState/search-state.service';
import { AccessCodesService } from '../../../Services/AccessCodes/Service/access-code.service';

interface LoginRequest {
    email: string;
    password: string;
    rememberMe: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  constructor(
    private cookieService: CookieService,
    private apiService: ApiService,
    private profileService: ProfileService,
    private rolesService: RolesService,
    private sessionService: SessionService,
    private searchStateService: SearchStateService,
    private accessCodesService: AccessCodesService,
  ) { 
    
    this.sessionService.logout$.subscribe(() => {
      this.logOut();
    });
  }

  public setAccessToken(token: AuthToken, rememberMe: boolean = false): void {
    if (rememberMe) {
      this.setCookie(token);
    } else {
      this.setSessionStorage(token);
    }
  }

  public setUserId(userId: string, rememberMe: boolean = false): void {
    if (rememberMe) {
      this.setUserIdCookie(userId);
    } else {
      sessionStorage.setItem('user_id', userId);
    }
  }

  public getUserId(): string | null {
    return sessionStorage.getItem('user_id') || 
           this.cookieService.get('user_id') || 
           null;
  }

  private setUserIdCookie(userId: string): void {
    const today = new Date();
    const expiry = today.setDate(today.getDate() + 1);

    const options: CookieOptions = {
      secure: environment.production,
      expires: expiry,
      path: '/',
      domain: environment.production ? window.location.hostname : '',
      sameSite: 'Strict'
    };

    this.cookieService.set(
      'user_id', userId,
      options.expires,
      options.path,
      options.domain,
      options.secure,
      options.sameSite
    );
  }

  public attemptLogin(payload: LoginRequest): Observable<string> {
    const url = `${environment.gatewayApi}/authproxy/login`;
    const options = {
      headers: {
        'Content-Type': 'application/vnd.reef.login-proxy-request-v1+json'
      }
    };
    return this.apiService.post(url, payload, options).pipe(
      map((res) => {
        const token: AuthToken = {
          access_token: res.access_token,
          type: res.token_type || 'Bearer'
        };
        this.setAccessToken(token, payload.rememberMe);
        return res;
      }),
      switchMap((res) => {
        this.setUserId(res.userId, payload.rememberMe);
        return this.profileService.fetchProfile(res.userId).pipe(
          catchError((profileError) => {
            this.clearTokens();
            throw new Error('Login failed: Unable to load user profile');
          })
        );
      }),
      map(() => 'logged in'),
      tap(() => {
        this.sessionService.startMonitoring();
      })
    );
  }

  public logOut(): void {
    this.sessionService.stopMonitoring();
    this.clearTokens();
    this.profileService.clearProfile();
    this.rolesService.clearRoles();
    this.searchStateService.clearAll();
    this.accessCodesService.clearCache();
  }

  /**
   * Handles authentication failures (expired token, invalid credentials, etc.)
   * Delegates to logOut to clean up all auth state when authentication fails
   * Called by HTTP interceptor on 401 (not 403 - auth is preserved for permission errors)
   */
  public handleAuthenticationFailure(): void {
    this.logOut();
  }

  public isAuthenticated(): boolean {
    const token = this.getAccessToken();
    return !!token && token.length > 0;
  }

  public getAccessToken(): string | null {
    return sessionStorage.getItem('access_token') || 
           this.cookieService.get('access_token') || 
           null;
  }

  public hasValidToken(): Observable<boolean> {
    const token = this.getAccessToken();
    if (!token) {
      return new Observable(observer => {
        observer.next(false);
        observer.complete();
      });
    }
    
    return new Observable(observer => {
      observer.next(true);
      observer.complete();
    });
  }

  private setSessionStorage(token: AuthToken): void {
    sessionStorage.setItem('access_token', token.access_token);
  }

  private clearTokens(): void {
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('user_id');
    
    // Delete cookies with same path and domain used when setting them
    const path = '/';
    const domain = environment.production ? window.location.hostname : '';
    
    this.cookieService.delete('access_token', path, domain);
    this.cookieService.delete('token_type', path, domain);
    this.cookieService.delete('user_id', path, domain);
  }

  private setCookie(token: AuthToken): void {

    const today = new Date();
    const tmr = today.setDate(today.getDate() + 1);

    const options: CookieOptions = {
        secure: environment.production,
        expires: tmr,
        path : '/',
        domain : environment.production ? window.location.hostname : '',
        sameSite : 'Strict'
    };

    this.cookieService.set(
      'access_token', token.access_token,
      options.expires,
      options.path,
      options.domain,
      options.secure,
      options.sameSite
    );

    this.cookieService.set(
      'token_type', token.type,
      options.expires,
      options.path,
      options.domain,
      options.secure,
      options.sameSite
    );
  }

}
